import { ProposalLetterhead, ProposalDocumentFooter } from "./ProposalBranding";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, FileText, ClipboardList, DollarSign, Users, Calendar, Sparkles, Target, CheckCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X } from "lucide-react";

export interface CleaningAIResults {
  companyIntroduction: string;
  scopeOfWork: string;
  cleaningSchedule: string;
  staffingEstimate: string;
  pricingBreakdown: string;
  addOnPricing: string;
  totalMonthlyCost: string;
  deepCleanCost: string;
  termsAndConditions: string;
}

interface CleaningProposalResultsProps {
  results: CleaningAIResults | null;
  isLoading: boolean;
  onResultsChange?: (results: CleaningAIResults) => void;
}

const sections = [
  { key: "companyIntroduction" as const, title: "A. Company Introduction", icon: FileText },
  { key: "scopeOfWork" as const, title: "B. Scope of Work", icon: ClipboardList },
  { key: "cleaningSchedule" as const, title: "C. Cleaning Schedule", icon: Calendar },
  { key: "staffingEstimate" as const, title: "D. Staffing & Labor Estimate", icon: Users },
  { key: "pricingBreakdown" as const, title: "E. Pricing Breakdown", icon: DollarSign },
  { key: "addOnPricing" as const, title: "F. Add-On Services", icon: Sparkles },
  { key: "totalMonthlyCost" as const, title: "G. Total Monthly Cost", icon: Target },
  { key: "deepCleanCost" as const, title: "H. One-Time Deep Clean Estimate", icon: Sparkles },
  { key: "termsAndConditions" as const, title: "I. Terms & Conditions", icon: CheckCircle },
];

const CleaningProposalResults = ({ results, isLoading, onResultsChange }: CleaningProposalResultsProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-lg font-medium">Generating Cleaning Proposal...</p>
        <p className="text-sm mt-1">Building scope of work, pricing, and schedule</p>
      </div>
    );
  }

  if (!results) return null;

  const startEdit = (key: string, value: string) => { setEditingKey(key); setEditValue(value || ""); };
  const saveEdit = () => {
    if (editingKey && results && onResultsChange) onResultsChange({ ...results, [editingKey]: editValue });
    setEditingKey(null);
  };
  const cancelEdit = () => { setEditingKey(null); setEditValue(""); };

  return (
    <div id="cleaning-proposal-content" className="proposal-document animate-fade-in">
      <ProposalLetterhead title="Cleaning Service Proposal" />

      {sections.map(({ key, title, icon: Icon }) => {
        const content = results[key];
        if (!content) return null;
        return (
          <Card key={key} data-pdf-block className="proposal-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-start justify-between gap-2 text-foreground text-sm">
                <span className="flex items-start gap-2 min-w-0">
                  <Icon className="h-4 w-4 shrink-0 text-primary" />
                  {title}
                </span>
                {onResultsChange && editingKey !== key && (
                  <Button data-html2canvas-ignore title={`Edit ${title}`} variant="ghost" size="sm" onClick={() => startEdit(key, content)} className="text-muted-foreground hover:text-foreground">
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editingKey === key ? (
                <div className="space-y-3">
                  <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="min-h-[120px] bg-secondary border-border text-foreground" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} className="gradient-primary text-primary-foreground"><Check className="h-4 w-4 mr-1" /> Save</Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none text-secondary-foreground whitespace-pre-wrap">
                  {content}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      <ProposalDocumentFooter />
    </div>
  );
};

export default CleaningProposalResults;
